import { Card } from '@/components/common'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { useSEO } from '@/hooks/useSEO'

const features = [
  {
    title: 'AI-Powered Smart Zoning',
    description:
      'Automatically create optimized city zones using advanced AI methods like Polar zoning, Iso zoning, K-Clustering, or manual zoning when needed. Each zone intelligently groups customers to ensure balanced workloads, better coverage, and scalable operations as your city grows.',
    image: '/images/izg-demo/Screenshot (19).png',
    useCase: 'City planners can leverage AI to automatically create efficient zones that balance workload and coverage across growing urban areas.',
  },
  {
    title: 'Scalable Control Across Cities',
    description:
      'Easily manage customers, zones, and routes across multiple cities from a single platform. Whether you operate in one city or hundreds, the system adapts dynamically, helping you maintain operational efficiency without manual planning overhead.',
    image: '/images/izg-demo/Screenshot (21).png',
    useCase: 'Multi-city operations can be managed from one dashboard, scaling seamlessly as your business expands to new markets.',
  },
  {
    title: 'Optimized Agent Routing at Street Level',
    description:
      'Generate highly efficient, real-world routes using OSRM and Google routing engines. Agents visit customers who are geographically closest, reducing travel time, fuel costs, and delays—while increasing daily visit capacity and productivity.',
    image: '/images/izg-demo/Screenshot (20).png',
    useCase: 'Field service teams can optimize daily routes to maximize visits per day while minimizing travel time and operational costs.',
  },
]

const useCases = [
  {
    title: 'Delivery Route Optimization',
    description:
      'Optimize delivery routes for drivers and agents across multiple zones. Reduce travel time and fuel costs while maximizing daily delivery capacity.',
    icon: '🚚',
  },
  {
    title: 'Field Service Management',
    description:
      'Manage service technicians and field agents with intelligent zone assignments and optimized routing. Balance workloads and improve response times.',
    icon: '🔧',
  },
  {
    title: 'Multi-City Operations',
    description:
      'Scale operations across multiple cities from a single platform. Automatically create zones, assign customers, and manage routes as you expand.',
    icon: '🌆',
  },
  {
    title: 'Real-time Asset Monitoring',
    description:
      'Monitor assets and vehicles in real-time with geofencing alerts. Get instant notifications when assets enter or exit designated zones.',
    icon: '📡',
  },
]

export const IZGDemo = () => {
  useSEO({
    title: 'IZG Demo - Intelligent Zoning & Geofencing',
    description: 'Discover IZG (Intelligent Zoning & Geofencing) - AI-powered smart zoning, real-time asset monitoring, and optimized agent routing. Manage customers, zones, and routes across multiple cities.',
    keywords: 'IZG, intelligent zoning, geofencing, asset tracking, zone management, route optimization, field service management, multi-city operations',
    url: 'https://bindertech.com/services/izg/demo',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'IZG - Intelligent Zoning & Geofencing',
      applicationCategory: 'BusinessApplication',
      description: 'AI-powered intelligent zoning and geofencing platform for real-time asset monitoring and route optimization',
      url: 'https://bindertech.com/services/izg/demo',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'SAR',
      },
    },
  })
  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark antialiased overflow-x-hidden transition-colors duration-300">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-900/20 dark:to-black/20 px-4 py-16 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12 mt-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-4">
                <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400">
                  radar
                </span>
                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  IZG
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Intelligent Zone Generator
              </h1>
              <p className="text-xl text-text-sub-light dark:text-text-sub-dark max-w-3xl mx-auto">
                Intelligent Zone Geofencing for immediate boundary control. Get alerts the second an
                asset moves.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Showcases */}
        <section className="py-16 md:py-24 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Key Features
              </h2>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                Explore the powerful capabilities of IZG with pre-filled data examples
              </p>
            </div>

            <div className="space-y-16">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`flex flex-col gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                >
                  <div className="flex-1">
                    <Card className="overflow-hidden">
                      <div className="aspect-video bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/30 dark:to-emerald-800/30">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Card>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <h3 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                      {feature.title}
                    </h3>
                    <p className="text-text-sub-light dark:text-text-sub-dark mb-6">
                      {feature.description}
                    </p>
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-300 mb-2">
                        Use Case Example:
                      </p>
                      <p className="text-sm text-emerald-800 dark:text-emerald-400">
                        {feature.useCase}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Grid */}
        <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-900/50 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Real-World Use Cases
              </h2>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                See how organizations leverage IZG to optimize operations and improve efficiency
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map((useCase, index) => (
                <Card key={index} className="text-center">
                  <div className="text-4xl mb-4">{useCase.icon}</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-main-light dark:text-text-main-dark">
                    {useCase.title}
                  </h3>
                  <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                    {useCase.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pre-filled Data Example
        <section className="py-16 md:py-24 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Sample Zone Management Dashboard
              </h2>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                Preview of the IZG zone management interface with pre-filled data
              </p>
            </div>

            <Card className="overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
                <div className="text-center">
                  <span className="material-symbols-outlined text-6xl text-gray-500 mb-4">
                    dashboard
                  </span>
                  <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                    /images/izg-demo/dashboard.png
                  </p>
                  <p className="text-xs text-text-sub-light dark:text-text-sub-dark mt-2">
                    Zone management dashboard screenshot placeholder
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section> */}

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-emerald-500/10 to-transparent dark:from-emerald-500/20 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-text-sub-light dark:text-text-sub-dark mb-8">
              Monitor assets and vehicles in real time with smart geofencing and instant alerts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:contact-us@binder.com
                ?subject=Service%20Subscription%20Request
                &body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20submit%20a%20service%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0ASERVICE%20REQUESTED:%0D%0AADDITIONAL%20DETAILS:%0D%0A%0D%0AThank%20you."
                className="flex h-12 items-center justify-center rounded-lg bg-primary px-8 text-base font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-blue-600 hover:shadow-xl hover:shadow-primary/30 active:scale-95"
              >
                Register
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

